/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import {
  Dialog,
  DialogTrigger,
  Header,
  Popover,
  Button as RAButton,
} from 'react-aria-components';
import { PlanSnapshotType } from '../../constants/links';

import {
  Animation,
  Button,
  Colors,
  ContentSection,
  createUniqueCustomProperties,
  Icon,
  Link,
  Shadows,
  Text,
  Typography,
  Zindexes,
} from '@zapier/design-system';
import { useState, useEffect, useCallback, Fragment } from 'react';

import { emitUserInteractionEvent, EventDetails } from '../../utils/avroEvents';
import { emitFullStoryEvent } from '../../utils/fullStoryEvents';

const Vars = createUniqueCustomProperties('Button', [
  'baseBackgroundColor',
  'baseBorderColor',
  'baseColor',
  'disabledBackgroundColor',
  'disabledBorderColor',
  'disabledColor',
  'disabledOutlineColor',
  'hoverBackgroundColor',
  'hoverBorderColor',
  'hoverColor',
  'hoverOutlineColor',
  'iconMargin',
  'focusBackgroundColor',
  'focusBorderColor',
  'focusColor',
  'focusOutlineColor',
  'selectedBackgroundColor',
  'selectedBorderColor',
  'selectedColor',
  'selectedOutlineColor',
  'buttonContentPadding',
]);

export const Styles = labelStyles('PlanSnapshotPopoverButton', {
  root: css`
    // All of the CSS Custom Properties that are used by this
    // component, ensured to be unique. Most are color related
    // and are set below via '[data-color]' selectors.
    ${Vars.baseBackgroundColor}: transparent;
    ${Vars.baseBorderColor}: transparent;
    ${Vars.baseColor}: #000;
    ${Vars.disabledBackgroundColor}: ${Colors.neutral200};
    ${Vars.disabledBorderColor}: ${Colors.neutral400};
    ${Vars.disabledColor}: ${Colors.neutral500};
    ${Vars.disabledOutlineColor}: transparent;
    ${Vars.hoverBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.hoverBorderColor}: var(${Vars.baseBorderColor});
    ${Vars.hoverColor}: var(${Vars.baseColor});
    ${Vars.hoverOutlineColor}: transparent;
    ${Vars.iconMargin}: 6px;
    ${Vars.focusBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.focusBorderColor}: transparent;
    ${Vars.focusColor}: var(${Vars.baseColor});
    ${Vars.focusOutlineColor}: var(${Vars.focusBackgroundColor});
    ${Vars.selectedBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.selectedBorderColor}: transparent;
    ${Vars.selectedColor}: var(${Vars.baseColor});
    ${Vars.selectedOutlineColor}: transparent;
    ${Vars.buttonContentPadding}: 0 8px 0 3px;

    background-color: var(${Vars.baseBackgroundColor});
    border-color: var(${Vars.baseBorderColor});
    color: var(${Vars.baseColor});
    ${Typography.paragraph3Semibold};
    height: 30px;
    border-radius: 3px;

    align-items: center;
    appearance: none;
    border-radius: 3px;
    border-style: solid;
    border-width: 1px;
    box-sizing: border-box;
    position: relative;
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    // Safari applies 2px margin to buttons this clears it
    margin: 0;
    min-width: 0;
    opacity: 1;
    text-align: center;
    text-decoration: none;
    text-transform: none;
    transition: all 200ms ease-in-out;
    vertical-align: middle;
    white-space: nowrap;

    &::-moz-focus-inner {
      padding: 0;
      border: 0;
    }

    // This is the outline that renders around the button when
    // its focused. Its a pseudo element to preserve transparency;
    // between the edge of the button and the edge of the outline.
    // Its defined here vs only in ':focus' so that it can transition
    // between states.
    &::before {
      content: '';
      position: absolute;
      pointer-events: none;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border: 2px solid transparent;
      border-radius: 5px;
      transition-property: all;
      transition-timing-function: ${Animation.transitionTimingFunction};
      transition-duration: 200ms;
    }

    &:hover:not([data-disabled]),
    a:hover & {
      background-color: var(${Vars.hoverBackgroundColor});
      border-color: var(${Vars.hoverBorderColor});
      box-shadow: ${Shadows.elevation6};
      color: var(${Vars.hoverColor});
    }

    &:focus:not([data-disabled]),
    a:focus & {
      background-color: var(${Vars.focusBackgroundColor});
      border-color: var(${Vars.focusBorderColor});
      color: var(${Vars.focusColor});
      outline: none;
      &::before {
        border-color: var(${Vars.focusOutlineColor});
        top: -5px;
        right: -5px;
        bottom: -5px;
        left: -5px;
      }
    }

    &[data-color='primary-alt'] {
      ${Vars.baseBackgroundColor}: ${Colors.neutral800};
      ${Vars.baseColor}: ${Colors.neutral100};
      ${Vars.hoverBackgroundColor}: ${Colors.neutral700};
    }

    &[data-disabled] {
      cursor: not-allowed;
      background-color: var(${Vars.disabledBackgroundColor});
      border-color: var(${Vars.disabledBorderColor});
      color: var(${Vars.disabledColor});
      outline-color: var(${Vars.disabledOutlineColor});
    }
  `,
  buttonContent: css`
    display: flex;
    align-items: center;
    min-width: 0;
    padding: var(${Vars.buttonContentPadding});
  `,
  buttonText: css`
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  `,
  buttonIcon: css`
    display: block;
    flex: none;
    margin-right: var(${Vars.iconMargin});
  `,
});

const dialogStyles = css({
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'column',
  boxSizing: 'border-box',
  zIndex: Zindexes.modalOverlay,
  borderRadius: '5px',
  border: `1px solid ${Colors.neutral300}`,
  boxShadow: `0px 5px 10px 0px #0000001A`,
  background: `${Colors.neutral100}`,
});
const contentStyles = css({
  padding: '37px 25px 0px 20px',
  minWidth: '500px',
});
const headerStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '15px',
});
const innerContentStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});
const featureContainerStyles = css({
  display: 'grid',
  gridTemplateColumns: 'auto auto',
  gap: '12px 0px',
  marginTop: '12px',
  marginBottom: '30px',
});
const featureStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
});
const footerStyles = css({
  background: Colors.blue100,
  padding: '15px 30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});
const closeButtonStyles = css({
  position: 'absolute',
  top: '8px',
  right: '8px',
  backgroundColor: 'transparent',
  border: '1px solid transparent',
  borderRadius: '3px',
  color: Colors.neutral600,
  padding: 0,
  outline: 'none',
  cursor: 'pointer',

  '&:focus, &:hover': {
    color: Colors.blue,
  },
});
const popoverStyles = css({
  '*': {
    '&:focus:not(a, button)': {
      outline: 'none',
    },
  },
});

type PlanSnapshotProps = PlanSnapshotType & { upgradeUrl: string };
const PlanSnapshotPopover = ({
  headerText,
  headerLink,
  footerText,
  featureSection,
  planType,
  triggerText,
  upgradeUrl,
}: PlanSnapshotProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const emitEvents = useCallback(
    (
      eventDetails: Omit<EventDetails, 'interaction_name'> & {
        interaction_name: string;
      }
    ) => {
      emitUserInteractionEvent(eventDetails);
      emitFullStoryEvent(eventDetails);
    },
    []
  );

  const onOpen = useCallback(() => {
    emitEvents({
      event_action: 'click',
      interaction_name: 'plan_snapshot_opened',
      option_selected: planType,
    });
    setIsOpen(true);
  }, [setIsOpen, emitEvents]);

  const onClose = useCallback(() => {
    emitEvents({
      event_action: 'click',
      interaction_name: 'plan_snapshot_closed',
      option_selected: planType,
    });
    setIsOpen(false);
  }, [setIsOpen, emitEvents]);

  useEffect(() => {
    emitEvents({
      event_action: 'in_view',
      interaction_name: 'plan_snapshot_rendered',
      option_selected: planType,
    });
  }, [emitEvents]);

  return (
    <DialogTrigger onOpenChange={(isOpen) => (isOpen ? onOpen() : onClose())}>
      <RAButton
        data-zds
        data-color="primary-alt"
        data-size="xsmall"
        css={Styles.root}
        {...(isOpen ? { 'aria-controls': 'plan-snapshot' } : {})}
      >
        <span css={Styles.buttonContent} data-zds>
          <span css={Styles.buttonIcon} data-zds>
            <Icon
              canAcceptPointerEvents={false}
              isBlock={true}
              name={isOpen ? 'arrowSmallUp' : 'arrowSmallDown'}
              size={24}
            />
          </span>
          <span css={Styles.buttonText} data-zds>
            {triggerText}
          </span>
        </span>
      </RAButton>

      <Popover placement="bottom start" css={popoverStyles}>
        <Dialog
          css={dialogStyles}
          id="plan-snapshot"
          aria-label={`Snapshot of the features of your ${planType} plan`}
        >
          {({ close }) => (
            <Fragment>
              <RAButton
                data-zds
                aria-label="Close dialog"
                css={closeButtonStyles}
                onPress={close}
              >
                <Icon isBlock={true} name="formX" size={24} />
              </RAButton>
              <ContentSection>
                <div css={contentStyles}>
                  <Header css={headerStyles}>
                    <div css={innerContentStyles}>
                      <Icon name="miscWallet" size={24} />
                      <Text type="subHeader3Bold">{headerText}</Text>
                    </div>
                    <Link
                      href={headerLink.href}
                      target={headerLink.target}
                      onClick={() =>
                        emitEvents({
                          event_action: 'click',
                          interaction_name: 'plan_snapshot_manage_plan_clicked',
                          option_selected: planType,
                        })
                      }
                    >
                      {headerLink.text}
                    </Link>
                  </Header>
                  <div>
                    <Text>{featureSection.title} </Text>
                  </div>
                  <div css={featureContainerStyles}>
                    {featureSection.features.map((feature) => (
                      <div key={feature.text} css={featureStyles}>
                        <Icon name={feature.icon} size={24} />
                        <Text>
                          {feature.href ? (
                            <Link
                              href={feature.href}
                              target="_blank"
                              onClick={() =>
                                emitEvents({
                                  event_action: 'click',
                                  interaction_name:
                                    'plan_snapshot_feature_clicked',
                                  option_selected: feature.text,
                                })
                              }
                            >
                              {feature.text}
                            </Link>
                          ) : (
                            feature.text
                          )}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>
              </ContentSection>
              <div id="footer" css={footerStyles}>
                <Text type="smallPrint1Medium">{footerText}</Text>
                <Button
                  size="xsmall"
                  href={upgradeUrl}
                  onClick={() =>
                    emitEvents({
                      event_action: 'click',
                      interaction_name: 'plan_snapshot_upgrade_clicked',
                      option_selected: planType,
                    })
                  }
                >
                  Upgrade
                </Button>
              </div>
            </Fragment>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};

export default PlanSnapshotPopover;
